/**
 * Crosstab Engine v2.0
 *
 * 仕様書に基づいた完全な再実装
 * - 複数値フィールドのサポート（Column × Measure Pivot）
 * - JSON-based key generation
 * - NULL値の適切な処理
 * - データ型を考慮したソート
 * - パーセンテージ計算
 */

import type { DataValue, DatasetWithData } from '../../../types';
import type { DatasetId } from '../../../types/id.types';
import type {
  CrosstabDataModel,
  CrosstabConfiguration,
  FieldDescriptor,
  ValueFieldDescriptor,
  RowHeader,
  ColumnHeader,
  CellValue,
  RowKey,
  ColumnKey,
  CellKey
} from './types';
import { DatasetAccessor } from '../DatasetAccessor';
import {
  generateRowKey,
  generateColumnKey,
  generateCellKey,
  getDisplayValues
} from './keyGeneration';
import { aggregate } from './aggregations';
import { sortRowHeaders, sortColumnHeaders } from './sorting';
import { calculateAllPercentages } from './percentageCalculator';
import logger from '../logger';

export class CrosstabEngine {
  /**
   * Crosstab集計を実行
   *
   * @param dataset - データを持つデータセット（呼び出し側で事前にevaluateが必要）
   * @param configuration - クロス集計の設定
   */
  async execute(
    dataset: DatasetWithData,
    configuration: CrosstabConfiguration
  ): Promise<CrosstabDataModel> {
    logger.info('[CrosstabEngine] Starting crosstab generation', {
      datasetId: dataset.id,
      rowFields: configuration.rowFields.map(f => f.fieldName),
      columnFields: configuration.columnFields.map(f => f.fieldName),
      valueFields: configuration.valueFields.map(f => f.fieldName)
    });

    const accessor = new DatasetAccessor(dataset);
    const rowCount = accessor.getRowCount();

    if (rowCount === 0) {
      logger.warn('[CrosstabEngine] Dataset has no rows');
      return this.createEmptyResult(dataset.id, configuration);
    }

    // Step 1: データの収集
    const {
      rowFieldsData,
      colFieldsData,
      valueFieldsData
    } = await this.collectFieldData(accessor, configuration);

    // Step 2: 集計処理
    const aggregationResult = this.performAggregation(
      rowCount,
      configuration,
      rowFieldsData,
      colFieldsData,
      valueFieldsData
    );

    // Step 3: ヘッダーの構築
    const rowHeaders = this.buildRowHeaders(
      aggregationResult.rowKeys,
      configuration.rowFields
    );
    const columnHeaders = this.buildColumnHeaders(
      aggregationResult.colKeys,
      configuration.columnFields,
      configuration.valueFields
    );

    // Step 4: ソート
    const sortedRowHeaders = sortRowHeaders(rowHeaders, configuration.rowFields);
    const sortedColumnHeaders = sortColumnHeaders(columnHeaders, configuration.columnFields);

    // Step 5: セルデータの構築
    const cells = this.buildCellValues(
      aggregationResult.cellAggregators,
      aggregationResult.cellRecordMaps,
      configuration.valueFields
    );

    // Step 6: 合計の計算
    const { rowTotals, columnTotals, grandTotal } = this.calculateTotals(
      configuration,
      aggregationResult.rowTotalAggregators,
      aggregationResult.colTotalAggregators,
      aggregationResult.grandTotalAggregators,
      aggregationResult.rowTotalRecordCounts,
      aggregationResult.colTotalRecordCounts,
      aggregationResult.grandTotalRecordCount
    );

    // Step 7: パーセンテージ計算
    // 集約関数の配列を渡すことで、AVG/MIN/MAXのフィールドだけパーセンテージを無効化
    const aggregations = configuration.valueFields.map(vf => vf.aggregation);
    calculateAllPercentages(
      cells,
      rowTotals,
      columnTotals,
      grandTotal,
      configuration.options.percentageMode,
      aggregations
    );

    // Step 8: 結果の構築
    const result: CrosstabDataModel = {
      metadata: {
        datasetId: dataset.id,
        generatedAt: new Date().toISOString(),
        version: '2.0'
      },
      configuration,
      structure: {
        rowHeaders: sortedRowHeaders,
        columnHeaders: sortedColumnHeaders
      },
      data: {
        cells,
        rowTotals: configuration.options.showRowTotals ? rowTotals : undefined,
        columnTotals: configuration.options.showColumnTotals ? columnTotals : undefined,
        grandTotal: configuration.options.showGrandTotal ? grandTotal : undefined
      },
      cellToRecords: aggregationResult.cellRecordMaps
    };

    logger.info('[CrosstabEngine] Crosstab generation completed', {
      rowCount: sortedRowHeaders.length,
      columnCount: sortedColumnHeaders.length,
      cellCount: cells.size,
      valueFieldCount: configuration.valueFields.length
    });

    return result;
  }

  /**
   * フィールドデータを収集
   */
  private async collectFieldData(
    accessor: DatasetAccessor,
    configuration: CrosstabConfiguration
  ) {
    const rowFieldsData: Record<string, DataValue[]> = {};
    const colFieldsData: Record<string, DataValue[]> = {};
    const valueFieldsData: Record<string, DataValue[]> = {};

    // 行フィールドのデータを取得
    for (const field of configuration.rowFields) {
      const data = await accessor.getColumnDataByIdAsync(field.fieldId);
      rowFieldsData[field.fieldName] = data;
    }

    // 列フィールドのデータを取得
    for (const field of configuration.columnFields) {
      const data = await accessor.getColumnDataByIdAsync(field.fieldId);
      colFieldsData[field.fieldName] = data;
    }

    // 値フィールドのデータを取得
    for (const field of configuration.valueFields) {
      // Countのみの場合はfieldIdがundefined、データ取得不要
      if (field.fieldId === undefined) {
        continue;
      }
      const data = await accessor.getColumnDataByIdAsync(field.fieldId);
      valueFieldsData[field.fieldName] = data;
    }

    return { rowFieldsData, colFieldsData, valueFieldsData };
  }

  /**
   * 集計処理を実行
   */
  private performAggregation(
    rowCount: number,
    configuration: CrosstabConfiguration,
    rowFieldsData: Record<string, DataValue[]>,
    colFieldsData: Record<string, DataValue[]>,
    valueFieldsData: Record<string, DataValue[]>
  ) {
    // 集計用のマップ
    const cellAggregators = new Map<CellKey, Map<string, DataValue[]>>();
    const cellRecordMaps = new Map<CellKey, number[]>();
    const rowTotalAggregators = new Map<RowKey, Map<string, DataValue[]>>();
    const colTotalAggregators = new Map<ColumnKey, Map<string, DataValue[]>>();
    const grandTotalAggregators = new Map<string, DataValue[]>();

    const rowTotalRecordCounts = new Map<RowKey, number>();
    const colTotalRecordCounts = new Map<ColumnKey, number>();
    let grandTotalRecordCount = 0;

    // ユニークなキーを収集
    const rowKeys = new Set<RowKey>();
    const colKeys = new Set<ColumnKey>();

    // 各行をスキャン
    for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
      // 行キーを生成
      const rowValues = configuration.rowFields.map(f => rowFieldsData[f.fieldName][rowIndex]);
      const rowKey = generateRowKey(rowValues);
      rowKeys.add(rowKey);

      // 列キーを生成
      const colValues = configuration.columnFields.map(f => colFieldsData[f.fieldName][rowIndex]);
      const colKey = generateColumnKey(colValues);
      colKeys.add(colKey);

      // セルキーを生成
      const cellKey = generateCellKey(rowKey, colKey);

      // セルの集計データを初期化
      if (!cellAggregators.has(cellKey)) {
        cellAggregators.set(cellKey, new Map());
        cellRecordMaps.set(cellKey, []);
      }

      // レコード番号を記録
      cellRecordMaps.get(cellKey)!.push(rowIndex);

      // 各値フィールドの値を収集
      for (const valueField of configuration.valueFields) {
        const cellAgg = cellAggregators.get(cellKey)!;

        if (!cellAgg.has(valueField.fieldName)) {
          cellAgg.set(valueField.fieldName, []);
        }

        // Countのみの場合はダミー値を追加（配列長がカウントされる）
        if (valueField.fieldId === undefined) {
          cellAgg.get(valueField.fieldName)!.push(1);
        } else {
          const value = valueFieldsData[valueField.fieldName][rowIndex];
          cellAgg.get(valueField.fieldName)!.push(value);
        }
      }

      // 行合計の収集
      if (configuration.options.showRowTotals) {
        if (!rowTotalAggregators.has(rowKey)) {
          rowTotalAggregators.set(rowKey, new Map());
          rowTotalRecordCounts.set(rowKey, 0);
        }

        rowTotalRecordCounts.set(rowKey, rowTotalRecordCounts.get(rowKey)! + 1);

        const rowTotalAgg = rowTotalAggregators.get(rowKey)!;
        for (const valueField of configuration.valueFields) {
          if (!rowTotalAgg.has(valueField.fieldName)) {
            rowTotalAgg.set(valueField.fieldName, []);
          }
          // Countのみの場合はダミー値を追加
          if (valueField.fieldId === undefined) {
            rowTotalAgg.get(valueField.fieldName)!.push(1);
          } else {
            const value = valueFieldsData[valueField.fieldName][rowIndex];
            rowTotalAgg.get(valueField.fieldName)!.push(value);
          }
        }
      }

      // 列合計の収集
      if (configuration.options.showColumnTotals) {
        if (!colTotalAggregators.has(colKey)) {
          colTotalAggregators.set(colKey, new Map());
          colTotalRecordCounts.set(colKey, 0);
        }

        colTotalRecordCounts.set(colKey, colTotalRecordCounts.get(colKey)! + 1);

        const colTotalAgg = colTotalAggregators.get(colKey)!;
        for (const valueField of configuration.valueFields) {
          if (!colTotalAgg.has(valueField.fieldName)) {
            colTotalAgg.set(valueField.fieldName, []);
          }
          // Countのみの場合はダミー値を追加
          if (valueField.fieldId === undefined) {
            colTotalAgg.get(valueField.fieldName)!.push(1);
          } else {
            const value = valueFieldsData[valueField.fieldName][rowIndex];
            colTotalAgg.get(valueField.fieldName)!.push(value);
          }
        }
      }

      // 総計の収集
      if (configuration.options.showGrandTotal) {
        grandTotalRecordCount++;

        for (const valueField of configuration.valueFields) {
          if (!grandTotalAggregators.has(valueField.fieldName)) {
            grandTotalAggregators.set(valueField.fieldName, []);
          }
          // Countのみの場合はダミー値を追加
          if (valueField.fieldId === undefined) {
            grandTotalAggregators.get(valueField.fieldName)!.push(1);
          } else {
            const value = valueFieldsData[valueField.fieldName][rowIndex];
            grandTotalAggregators.get(valueField.fieldName)!.push(value);
          }
        }
      }
    }

    return {
      cellAggregators,
      cellRecordMaps,
      rowTotalAggregators,
      colTotalAggregators,
      grandTotalAggregators,
      rowTotalRecordCounts,
      colTotalRecordCounts,
      grandTotalRecordCount,
      rowKeys,
      colKeys
    };
  }

  /**
   * 行ヘッダーを構築
   */
  private buildRowHeaders(
    rowKeys: Set<RowKey>,
    _rowFields: FieldDescriptor[]
  ): RowHeader[] {
    return Array.from(rowKeys).map(key => {
      const values = JSON.parse(key) as DataValue[];
      return {
        key,
        displayValues: getDisplayValues(values),
        level: 0,
        isSubtotal: false
      };
    });
  }

  /**
   * 列ヘッダーを構築（Column × Measure Pivot）
   *
   * 複数値フィールドがある場合、各Column値に対して全てのMeasureを列として展開する
   */
  private buildColumnHeaders(
    colKeys: Set<ColumnKey>,
    _columnFields: FieldDescriptor[],
    valueFields: ValueFieldDescriptor[]
  ): ColumnHeader[] {
    const headers: ColumnHeader[] = [];

    for (const colKey of Array.from(colKeys)) {
      const columnValues = JSON.parse(colKey) as DataValue[];
      const columnDisplayValues = getDisplayValues(columnValues);

      // 各値フィールドに対して列を生成（Column × Measure Pivot）
      for (let measureIndex = 0; measureIndex < valueFields.length; measureIndex++) {
        const valueField = valueFields[measureIndex];

        headers.push({
          key: colKey,
          displayValues: columnDisplayValues,
          level: 0,
          isSubtotal: false,
          measureFieldName: valueField.fieldName,
          measureFieldAlias: valueField.alias,
          measureAggregation: valueField.aggregation,
          measureIndex
        });
      }
    }

    return headers;
  }

  /**
   * セル値を構築
   */
  private buildCellValues(
    cellAggregators: Map<CellKey, Map<string, DataValue[]>>,
    cellRecordMaps: Map<CellKey, number[]>,
    valueFields: ValueFieldDescriptor[]
  ): Map<CellKey, CellValue> {
    const cells = new Map<CellKey, CellValue>();

    for (const [cellKey, aggregatorMap] of cellAggregators.entries()) {
      const values: number[] = [];

      // 各値フィールドを集計
      for (const valueField of valueFields) {
        const rawValues = aggregatorMap.get(valueField.fieldName) || [];
        const aggregatedValue = aggregate(valueField.aggregation, rawValues);
        values.push(aggregatedValue ?? 0);
      }

      const recordCount = cellRecordMaps.get(cellKey)?.length || 0;

      cells.set(cellKey, {
        values,
        recordCount,
        isEmpty: recordCount === 0
      });
    }

    return cells;
  }

  /**
   * 合計を計算
   */
  private calculateTotals(
    configuration: CrosstabConfiguration,
    rowTotalAggregators: Map<RowKey, Map<string, DataValue[]>>,
    colTotalAggregators: Map<ColumnKey, Map<string, DataValue[]>>,
    grandTotalAggregators: Map<string, DataValue[]>,
    rowTotalRecordCounts: Map<RowKey, number>,
    colTotalRecordCounts: Map<ColumnKey, number>,
    grandTotalRecordCount: number
  ) {
    const rowTotals = new Map<RowKey, CellValue>();
    const columnTotals = new Map<ColumnKey, CellValue>();
    let grandTotal: CellValue | undefined;

    // 行合計
    if (configuration.options.showRowTotals) {
      for (const [rowKey, aggregatorMap] of rowTotalAggregators.entries()) {
        const values: number[] = [];
        for (const valueField of configuration.valueFields) {
          const rawValues = aggregatorMap.get(valueField.fieldName) || [];
          const aggregatedValue = aggregate(valueField.aggregation, rawValues);
          values.push(aggregatedValue ?? 0);
        }

        rowTotals.set(rowKey, {
          values,
          recordCount: rowTotalRecordCounts.get(rowKey) || 0,
          isEmpty: false
        });
      }
    }

    // 列合計
    if (configuration.options.showColumnTotals) {
      for (const [colKey, aggregatorMap] of colTotalAggregators.entries()) {
        const values: number[] = [];
        for (const valueField of configuration.valueFields) {
          const rawValues = aggregatorMap.get(valueField.fieldName) || [];
          const aggregatedValue = aggregate(valueField.aggregation, rawValues);
          values.push(aggregatedValue ?? 0);
        }

        columnTotals.set(colKey, {
          values,
          recordCount: colTotalRecordCounts.get(colKey) || 0,
          isEmpty: false
        });
      }
    }

    // 総計
    if (configuration.options.showGrandTotal) {
      const values: number[] = [];
      for (const valueField of configuration.valueFields) {
        const rawValues = grandTotalAggregators.get(valueField.fieldName) || [];
        const aggregatedValue = aggregate(valueField.aggregation, rawValues);
        values.push(aggregatedValue ?? 0);
      }

      grandTotal = {
        values,
        recordCount: grandTotalRecordCount,
        isEmpty: false
      };
    }

    return { rowTotals, columnTotals, grandTotal };
  }

  /**
   * 空の結果を作成
   */
  private createEmptyResult(
    datasetId: DatasetId,
    configuration: CrosstabConfiguration
  ): CrosstabDataModel {
    return {
      metadata: {
        datasetId,
        generatedAt: new Date().toISOString(),
        version: '2.0'
      },
      configuration,
      structure: {
        rowHeaders: [],
        columnHeaders: []
      },
      data: {
        cells: new Map()
      },
      cellToRecords: new Map()
    };
  }
}
